import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import Modal from '../components/Modal';
import CustomerSearchSelect from '../components/CustomerSearchSelect';
import './Invoices.css';

// Phone number formatting utility
const formatPhoneNumber = (value) => {
  const phoneNumber = value.replace(/\D/g, '');
  if (phoneNumber.length <= 3) {
    return phoneNumber;
  } else if (phoneNumber.length <= 6) {
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
  } else {
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  }
};

const Quotes = () => {
  const navigate = useNavigate();

  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState({ packages: [], addons: [] });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [editingQuote, setEditingQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [copyQuoteLabel, setCopyQuoteLabel] = useState('Text Quote');
  const [converting, setConverting] = useState(false);

  const [formData, setFormData] = useState({
    customer_id: '',
    vehicles: [{ id: Date.now(), label: '', vehicle_type: 'car', selectedServices: [] }],
    tax_rate: 0,
    tax_enabled: false,
    notes: ''
  });

  const [newCustomerData, setNewCustomerData] = useState({
    first_name: '', last_name: '', phone: '', email: '',
    addVehicle: false, vehicle_year: '', vehicle_make: '',
    vehicle_model: '', vehicle_color: '', vehicle_type: 'sedan'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [quotesRes, customersRes, servicesRes] = await Promise.all([
        fetch('/api/quotes'),
        fetch('/api/customers'),
        fetch('/api/services/grouped')
      ]);

      const [quotesData, customersData, servicesData] = await Promise.all([
        quotesRes.json(),
        customersRes.json(),
        servicesRes.json()
      ]);

      setQuotes(quotesData);
      setCustomers(customersData);
      setServices(servicesData);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuoteDetail = async (id) => {
    try {
      const response = await fetch(`/api/quotes/${id}`);
      const data = await response.json();
      setSelectedQuote(data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleCreateCustomer = async () => {
    if (!newCustomerData.first_name || !newCustomerData.last_name || !newCustomerData.phone) {
      alert('Please fill in first name, last name, and phone');
      return;
    }

    if (newCustomerData.addVehicle && (!newCustomerData.vehicle_make || !newCustomerData.vehicle_model)) {
      alert('Please fill in vehicle make and model');
      return;
    }

    try {
      const response = await fetch('/api/customers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: newCustomerData.first_name,
          last_name: newCustomerData.last_name,
          phone: newCustomerData.phone,
          email: newCustomerData.email
        })
      });

      if (response.ok) {
        const newCustomer = await response.json();

        if (newCustomerData.addVehicle && newCustomerData.vehicle_make && newCustomerData.vehicle_model) {
          await fetch('/api/vehicles', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              customer_id: newCustomer.id,
              year: newCustomerData.vehicle_year || null,
              make: newCustomerData.vehicle_make,
              model: newCustomerData.vehicle_model,
              color: newCustomerData.vehicle_color || null,
              vehicle_type: newCustomerData.vehicle_type
            })
          });
        }

        const customersRes = await fetch('/api/customers');
        const customersData = await customersRes.json();
        setCustomers(customersData);
        setFormData({ ...formData, customer_id: newCustomer.id.toString() });
        setShowNewCustomerForm(false);
        setNewCustomerData({
          first_name: '', last_name: '', phone: '', email: '',
          addVehicle: false, vehicle_year: '', vehicle_make: '',
          vehicle_model: '', vehicle_color: '', vehicle_type: 'sedan'
        });
      }
    } catch (error) {
      console.error('Error creating customer:', error);
    }
  };

  const fetchServicesForPackages = async (selectedPackageIds) => {
    try {
      const packageId = selectedPackageIds.length > 0 ? selectedPackageIds[0] : null;
      const queryParam = packageId ? `?package_id=${packageId}` : '';
      const servicesRes = await fetch(`/api/services/grouped${queryParam}`);
      const servicesData = await servicesRes.json();
      setServices(servicesData);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const addVehicle = () => {
    setFormData({
      ...formData,
      vehicles: [
        ...formData.vehicles,
        { id: Date.now(), label: '', vehicle_type: 'car', selectedServices: [] }
      ]
    });
  };

  const removeVehicle = (vehicleIndex) => {
    setFormData({
      ...formData,
      vehicles: formData.vehicles.filter((_, i) => i !== vehicleIndex)
    });
  };

  const updateVehicleLabel = (vehicleIndex, label) => {
    const updatedVehicles = formData.vehicles.map((v, i) =>
      i === vehicleIndex ? { ...v, label } : v
    );
    setFormData({ ...formData, vehicles: updatedVehicles });
  };

  const toggleService = (vehicleIndex, service) => {
    const vehicle = formData.vehicles[vehicleIndex];
    const isSelected = vehicle.selectedServices.find(s => s.id === service.id);
    let newSelectedServices;

    if (isSelected) {
      newSelectedServices = vehicle.selectedServices.filter(s => s.id !== service.id);
    } else {
      const price = vehicle.vehicle_type === 'truck'
        ? (service.base_price_truck || service.base_price_car)
        : service.base_price_car;

      newSelectedServices = [...vehicle.selectedServices, {
        id: service.id,
        name: service.name,
        price: price,
        category: service.category,
        customNote: ''
      }];
    }

    const updatedVehicles = formData.vehicles.map((v, i) =>
      i === vehicleIndex ? { ...v, selectedServices: newSelectedServices } : v
    );
    setFormData({ ...formData, vehicles: updatedVehicles });

    if (service.category === 'package') {
      const selectedPackageIds = newSelectedServices
        .filter(s => s.category === 'package')
        .map(s => s.id);
      fetchServicesForPackages(selectedPackageIds);
    }
  };

  const isServiceSelected = (vehicleIndex, serviceId) => {
    return formData.vehicles[vehicleIndex]?.selectedServices.some(s => s.id === serviceId);
  };

  const updateServicePrice = (vehicleIndex, serviceId, newPrice) => {
    const updatedVehicles = formData.vehicles.map((v, i) =>
      i === vehicleIndex ? {
        ...v,
        selectedServices: v.selectedServices.map(s =>
          s.id === serviceId ? { ...s, price: parseFloat(newPrice) || 0 } : s
        )
      } : v
    );
    setFormData({ ...formData, vehicles: updatedVehicles });
  };

  const updateServiceNote = (vehicleIndex, serviceId, note) => {
    const updatedVehicles = formData.vehicles.map((v, i) =>
      i === vehicleIndex ? {
        ...v,
        selectedServices: v.selectedServices.map(s =>
          s.id === serviceId ? { ...s, customNote: note } : s
        )
      } : v
    );
    setFormData({ ...formData, vehicles: updatedVehicles });
  };

  const handleVehicleTypeChange = (vehicleIndex, newVehicleType) => {
    const vehicle = formData.vehicles[vehicleIndex];
    const updatedServices = vehicle.selectedServices.map(selectedService => {
      const fullService = [...services.packages, ...services.addons].find(s => s.id === selectedService.id);
      if (fullService) {
        const newPrice = newVehicleType === 'truck'
          ? (fullService.base_price_truck || fullService.base_price_car)
          : fullService.base_price_car;
        return { ...selectedService, price: newPrice };
      }
      return selectedService;
    });

    const updatedVehicles = formData.vehicles.map((v, i) =>
      i === vehicleIndex ? { ...v, vehicle_type: newVehicleType, selectedServices: updatedServices } : v
    );
    setFormData({ ...formData, vehicles: updatedVehicles });
  };

  const calculateSubtotal = () => {
    return formData.vehicles.reduce((total, vehicle) =>
      total + vehicle.selectedServices.reduce((sum, s) => sum + (s.price || 0), 0), 0
    );
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      vehicles: [{ id: Date.now(), label: '', vehicle_type: 'car', selectedServices: [] }],
      tax_rate: 0,
      tax_enabled: false,
      notes: ''
    });
    setEditingQuote(null);
    setShowNewCustomerForm(false);
    setNewCustomerData({
      first_name: '', last_name: '', phone: '', email: '',
      addVehicle: false, vehicle_year: '', vehicle_make: '',
      vehicle_model: '', vehicle_color: '', vehicle_type: 'sedan'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    try {
      const items = formData.vehicles.flatMap(vehicle =>
        vehicle.selectedServices.map(service => {
          const baseName = service.customNote ? `${service.name} — ${service.customNote}` : service.name;
          return {
            description: vehicle.label ? `${vehicle.label} — ${baseName}` : baseName,
            quantity: 1,
            unit_price: service.price
          };
        })
      );

      if (editingQuote) {
        const response = await fetch(`/api/quotes/${editingQuote.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items,
            tax_rate: formData.tax_rate,
            notes: formData.notes
          })
        });

        if (response.ok) {
          setShowModal(false);
          resetForm();
          fetchData();
        }
      } else {
        const response = await fetch('/api/quotes', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customer_id: formData.customer_id,
            items,
            tax_rate: formData.tax_rate,
            notes: formData.notes
          })
        });

        if (response.ok) {
          setShowModal(false);
          resetForm();
          fetchData();
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditQuote = async (quote) => {
    try {
      const response = await fetch(`/api/quotes/${quote.id}`);
      const data = await response.json();

      setEditingQuote(data);

      const selectedServices = data.items.map(item => ({
        id: null,
        name: item.description,
        price: parseFloat(item.unit_price),
        category: 'addon'
      }));

      setFormData({
        customer_id: data.customer_id.toString(),
        vehicles: [{ id: Date.now(), label: '', vehicle_type: 'car', selectedServices }],
        tax_rate: data.tax_rate,
        tax_enabled: data.tax_rate > 0,
        notes: data.notes || ''
      });

      setShowDetailModal(false);
      setShowModal(true);
    } catch (error) {
      console.error('Error loading quote for edit:', error);
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    if (!window.confirm('Move this quote to trash? You can restore it later from the Trash page.')) {
      return;
    }

    try {
      const response = await fetch(`/api/quotes/${quoteId}`, { method: 'DELETE' });
      if (response.ok) {
        setShowDetailModal(false);
        setSelectedQuote(null);
        fetchData();
      } else {
        const error = await response.json();
        alert(`Failed to delete quote: ${error.error}`);
      }
    } catch (error) {
      console.error('Error deleting quote:', error);
      alert('Failed to delete quote. Please try again.');
    }
  };

  const handleConvertToInvoice = async (quoteId) => {
    if (!window.confirm('Convert this quote into a real invoice? It will get a new invoice number and move to the Invoices page.')) {
      return;
    }

    setConverting(true);
    try {
      const response = await fetch(`/api/quotes/${quoteId}/convert-to-invoice`, { method: 'POST' });
      if (response.ok) {
        const invoice = await response.json();
        alert(`Converted to Invoice #${invoice.invoice_number}`);
        navigate('/invoices');
      } else {
        const error = await response.json();
        alert(`Failed to convert quote: ${error.error}`);
      }
    } catch (error) {
      console.error('Error converting quote:', error);
      alert('Failed to convert quote. Please try again.');
    } finally {
      setConverting(false);
    }
  };

  const handleCopyQuote = async (quoteId) => {
    try {
      const res = await fetch(`/api/invoices/${quoteId}/public-link`, { method: 'POST' });
      const data = await res.json();
      if (data.url) {
        const firstName = selectedQuote?.first_name || '';
        const message = `Hi ${firstName}, thank you for reaching out! Here's a quote for the services you asked about: ${data.url}\n\nLet us know if you'd like to schedule — we'd love to help!`;

        if (navigator.share) {
          await navigator.share({ text: message });
        } else {
          let copied = false;
          if (navigator.clipboard && navigator.clipboard.writeText) {
            try {
              await navigator.clipboard.writeText(message);
              copied = true;
            } catch (e) {
              // fall through to execCommand fallback
            }
          }
          if (!copied) {
            const textarea = document.createElement('textarea');
            textarea.value = message;
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            copied = document.execCommand('copy');
            document.body.removeChild(textarea);
          }
          if (copied) {
            setCopyQuoteLabel('Copied!');
            setTimeout(() => setCopyQuoteLabel('Text Quote'), 2500);
          } else {
            alert('Failed to copy quote link. Please try again.');
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        alert('Failed to copy quote link. Please try again.');
      }
    }
  };

  const subtotal = calculateSubtotal();
  const taxAmount = subtotal * formData.tax_rate;
  const total = subtotal + taxAmount;

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="invoices-page">
      <div className="page-header">
        <h1 className="page-header__title">Quotes</h1>
        <div className="page-header__actions">
          <button className="btn btn--primary" onClick={() => setShowModal(true)}>
            + Create Quote
          </button>
        </div>
      </div>

      {quotes.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M4 2v20l3-2 3 2 3-2 3 2 3-2 3 2V2l-3 2-3-2-3 2-3-2-3 2-3-2z" />
              <path d="M8 10h8" />
              <path d="M8 14h4" />
            </svg>
          </div>
          <h3 className="empty-state__title">No quotes yet</h3>
          <p>Create a quote when a customer asks about pricing</p>
          <button className="btn btn--primary mt-md" onClick={() => setShowModal(true)}>
            + Create Quote
          </button>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Quote #</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Amount</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((quote) => (
                <tr
                  key={quote.id}
                  className="clickable-row"
                  onClick={() => fetchQuoteDetail(quote.id)}
                >
                  <td className="invoice-number">{quote.invoice_number}</td>
                  <td>{quote.first_name} {quote.last_name}</td>
                  <td>{format(new Date(quote.created_at), 'MMM d, yyyy')}</td>
                  <td className="invoice-amount">${parseFloat(quote.total).toFixed(2)}</td>
                  <td>
                    <div className="invoice-actions" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="btn btn--primary btn--small"
                        disabled={converting}
                        onClick={() => handleConvertToInvoice(quote.id)}
                      >
                        Convert to Invoice
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Quote Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); resetForm(); }}
        title={editingQuote ? "Edit Quote" : "Create Quote"}
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Customer *</label>
            {!showNewCustomerForm ? (
              <div className="customer-select-wrapper">
                <CustomerSearchSelect
                  customers={customers}
                  value={formData.customer_id}
                  onChange={(id) => setFormData({ ...formData, customer_id: id })}
                  disabled={!!editingQuote}
                />
                {!editingQuote && (
                  <button
                    type="button"
                    className="btn btn--secondary btn--small new-customer-btn"
                    onClick={() => setShowNewCustomerForm(true)}
                  >
                    + New Customer
                  </button>
                )}
              </div>
            ) : (
              <div className="new-customer-form">
                <div className="form-row">
                  <input
                    type="text"
                    className="form-input"
                    placeholder="First Name *"
                    value={newCustomerData.first_name}
                    onChange={(e) => setNewCustomerData({ ...newCustomerData, first_name: e.target.value })}
                  />
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Last Name *"
                    value={newCustomerData.last_name}
                    onChange={(e) => setNewCustomerData({ ...newCustomerData, last_name: e.target.value })}
                  />
                </div>
                <div className="form-row">
                  <input
                    type="tel"
                    className="form-input"
                    placeholder="Phone * (XXX-XXX-XXXX)"
                    value={newCustomerData.phone}
                    onChange={(e) => setNewCustomerData({ ...newCustomerData, phone: formatPhoneNumber(e.target.value) })}
                  />
                  <input
                    type="email"
                    className="form-input"
                    placeholder="Email (optional)"
                    value={newCustomerData.email}
                    onChange={(e) => setNewCustomerData({ ...newCustomerData, email: e.target.value })}
                  />
                </div>

                <div className="add-vehicle-toggle">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={newCustomerData.addVehicle}
                      onChange={(e) => setNewCustomerData({ ...newCustomerData, addVehicle: e.target.checked })}
                    />
                    <span>Add a vehicle for this customer</span>
                  </label>
                </div>

                {newCustomerData.addVehicle && (
                  <div className="vehicle-fields">
                    <div className="form-row form-row--3">
                      <input
                        type="number"
                        className="form-input"
                        placeholder="Year"
                        value={newCustomerData.vehicle_year}
                        onChange={(e) => setNewCustomerData({ ...newCustomerData, vehicle_year: e.target.value })}
                      />
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Make *"
                        value={newCustomerData.vehicle_make}
                        onChange={(e) => setNewCustomerData({ ...newCustomerData, vehicle_make: e.target.value })}
                      />
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Model *"
                        value={newCustomerData.vehicle_model}
                        onChange={(e) => setNewCustomerData({ ...newCustomerData, vehicle_model: e.target.value })}
                      />
                    </div>
                    <div className="form-row">
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Color"
                        value={newCustomerData.vehicle_color}
                        onChange={(e) => setNewCustomerData({ ...newCustomerData, vehicle_color: e.target.value })}
                      />
                      <select
                        className="form-select"
                        value={newCustomerData.vehicle_type}
                        onChange={(e) => setNewCustomerData({ ...newCustomerData, vehicle_type: e.target.value })}
                      >
                        <option value="sedan">Sedan</option>
                        <option value="coupe">Coupe</option>
                        <option value="hatchback">Hatchback</option>
                        <option value="truck">Truck</option>
                        <option value="suv">SUV</option>
                        <option value="van">Van</option>
                        <option value="motorcycle">Motorcycle</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                )}

                <div className="new-customer-actions">
                  <button
                    type="button"
                    className="btn btn--secondary btn--small"
                    onClick={() => {
                      setShowNewCustomerForm(false);
                      setNewCustomerData({
                        first_name: '', last_name: '', phone: '', email: '',
                        addVehicle: false, vehicle_year: '', vehicle_make: '',
                        vehicle_model: '', vehicle_color: '', vehicle_type: 'sedan'
                      });
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary btn--small"
                    onClick={handleCreateCustomer}
                  >
                    Add Customer
                  </button>
                </div>
              </div>
            )}
          </div>

          {formData.vehicles.map((vehicle, vIndex) => (
            <div key={vehicle.id} className={`vehicle-section${formData.vehicles.length > 1 ? ' vehicle-section--bordered' : ''}`}>
              {formData.vehicles.length > 1 && (
                <div className="vehicle-section__header">
                  <span className="vehicle-section__title">Vehicle {vIndex + 1}</span>
                  <button
                    type="button"
                    className="btn btn--danger btn--small"
                    onClick={() => removeVehicle(vIndex)}
                  >
                    Remove
                  </button>
                </div>
              )}

              {/* Vehicle Label */}
              <div className="form-group">
                <input
                  type="text"
                  className="form-input"
                  placeholder='Vehicle label (e.g. Tesla, Wife&apos;s Porsche...)'
                  value={vehicle.label}
                  onChange={(e) => updateVehicleLabel(vIndex, e.target.value)}
                />
                <small className="form-hint">Optional — appears on the quote before each service (e.g. "Tesla — Full Detail")</small>
              </div>

              {/* Vehicle Type */}
              <div className="form-group">
                <label className="form-label">Vehicle Type</label>
                <div className="vehicle-type-selector">
                  <button
                    type="button"
                    className={`vehicle-type-btn ${vehicle.vehicle_type === 'car' ? 'vehicle-type-btn--active' : ''}`}
                    onClick={() => handleVehicleTypeChange(vIndex, 'car')}
                  >
                    Car
                  </button>
                  <button
                    type="button"
                    className={`vehicle-type-btn ${vehicle.vehicle_type === 'truck' ? 'vehicle-type-btn--active' : ''}`}
                    onClick={() => handleVehicleTypeChange(vIndex, 'truck')}
                  >
                    SUV / Truck
                  </button>
                  <button
                    type="button"
                    className={`vehicle-type-btn ${vehicle.vehicle_type !== 'car' && vehicle.vehicle_type !== 'truck' ? 'vehicle-type-btn--active' : ''}`}
                    onClick={() => handleVehicleTypeChange(vIndex, 'other')}
                  >
                    Other
                  </button>
                </div>
                {vehicle.vehicle_type !== 'car' && vehicle.vehicle_type !== 'truck' && (
                  <input
                    type="text"
                    className="form-input vehicle-type-input"
                    placeholder="Enter vehicle type (e.g., Motorcycle, RV, Boat)"
                    value={vehicle.vehicle_type === 'other' ? '' : vehicle.vehicle_type}
                    onChange={(e) => {
                      const updatedVehicles = formData.vehicles.map((v, i) =>
                        i === vIndex ? { ...v, vehicle_type: e.target.value || 'other' } : v
                      );
                      setFormData({ ...formData, vehicles: updatedVehicles });
                    }}
                  />
                )}
              </div>

              {/* Packages */}
              <div className="form-group">
                <label className="form-label">Packages</label>
                <div className="service-selector">
                  {services.packages.map((service) => {
                    return (
                      <div
                        key={service.id}
                        className={`service-card ${isServiceSelected(vIndex, service.id) ? 'service-card--selected' : ''}`}
                        onClick={() => toggleService(vIndex, service)}
                      >
                        <div className="service-card__header">
                          <span className="service-card__name">{service.name}</span>
                          {service.base_price_truck && service.base_price_truck !== service.base_price_car ? (
                            <div className="service-card__pricing-simple">
                              <span className={`service-card__price ${vehicle.vehicle_type === 'car' ? 'service-card__price--active' : ''}`}>
                                ${service.base_price_car}
                              </span>
                              <span className="service-card__price-separator">/</span>
                              <span className={`service-card__price ${vehicle.vehicle_type === 'truck' ? 'service-card__price--active' : ''}`}>
                                ${service.base_price_truck}
                              </span>
                            </div>
                          ) : (
                            <span className="service-card__price">${service.base_price_car}</span>
                          )}
                        </div>
                        {service.description && (
                          <p className="service-card__desc">{service.description}</p>
                        )}
                        {isServiceSelected(vIndex, service.id) && (
                          <div className="service-card__check">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                            </svg>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Add-ons */}
              <div className="form-group">
                <label className="form-label">Add-ons</label>
                <div className="service-selector service-selector--addons">
                  {services.addons.map((service) => {
                    const currentPrice = vehicle.vehicle_type === 'truck'
                      ? (service.base_price_truck || service.base_price_car)
                      : service.base_price_car;

                    return (
                      <div
                        key={service.id}
                        className={`addon-chip ${isServiceSelected(vIndex, service.id) ? 'addon-chip--selected' : ''}`}
                        onClick={() => toggleService(vIndex, service)}
                      >
                        <span className="addon-chip__name">{service.name}</span>
                        <span className="addon-chip__price">+${currentPrice}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Selected Items for this vehicle */}
              {vehicle.selectedServices.length > 0 && (
                <div className="selected-services">
                  <label className="form-label">Selected Items</label>
                  {vehicle.selectedServices.map((service, sIndex) => (
                    <div key={service.id ?? sIndex} className="selected-service-item">
                      <div className="selected-service-row">
                        <span className="selected-service-name">
                          {service.category === 'addon' && '+ '}
                          {service.name}
                        </span>
                        <div className="selected-service-price">
                          <span>$</span>
                          <input
                            type="number"
                            step="0.01"
                            value={service.price}
                            onChange={(e) => updateServicePrice(vIndex, service.id, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="price-input"
                          />
                        </div>
                        <button
                          type="button"
                          className="btn btn--danger btn--icon"
                          onClick={() => toggleService(vIndex, service)}
                        >
                          &times;
                        </button>
                      </div>
                      {service.name === 'Custom Inquiry' && (
                        <input
                          type="text"
                          className="form-input custom-inquiry-note"
                          placeholder="Briefly describe the job (shows on quote)"
                          value={service.customNote || ''}
                          onChange={(e) => updateServiceNote(vIndex, service.id, e.target.value)}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Add Another Vehicle */}
          <div className="form-group">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={addVehicle}
            >
              + Add Another Vehicle
            </button>
          </div>

          {/* Tax Toggle */}
          <div className="form-group">
            <div className="tax-toggle">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.tax_enabled}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    setFormData({
                      ...formData,
                      tax_enabled: enabled,
                      tax_rate: enabled ? 0.0825 : 0
                    });
                  }}
                />
                <span>Apply Sales Tax</span>
              </label>
              {formData.tax_enabled && (
                <div className="tax-rate-input">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={(formData.tax_rate * 100).toFixed(2)}
                    onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) / 100 || 0 })}
                    className="form-input tax-input"
                  />
                  <span>%</span>
                </div>
              )}
            </div>
          </div>

          <div className="invoice-summary">
            <div className="invoice-summary__row">
              <span>Subtotal</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            {formData.tax_enabled && (
              <div className="invoice-summary__row">
                <span>Tax ({(formData.tax_rate * 100).toFixed(2)}%)</span>
                <span>${taxAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="invoice-summary__row invoice-summary__row--total">
              <span>Estimated Total</span>
              <span>${total.toFixed(2)}</span>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea
              className="form-textarea"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows="2"
            />
          </div>

          <div className="modal__footer">
            <button type="button" className="btn btn--secondary" onClick={() => { setShowModal(false); resetForm(); }}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary" disabled={submitting}>
              {submitting ? 'Saving...' : (editingQuote ? 'Update Quote' : 'Create Quote')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Quote Detail Modal */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title={`Quote ${selectedQuote?.invoice_number || ''}`}
      >
        {selectedQuote && (
          <div className="invoice-detail">
            <div className="invoice-detail__header">
              <div>
                <h3 className="invoice-detail__company">POLARIS</h3>
                <p className="invoice-detail__tagline">Professional Detailing</p>
              </div>
              <span className="badge badge--draft">Quote</span>
            </div>

            <div className="invoice-detail__info">
              <div>
                <p className="invoice-detail__label">Prepared For</p>
                <p className="invoice-detail__value">
                  {selectedQuote.first_name} {selectedQuote.last_name}
                </p>
                {selectedQuote.phone && (
                  <p className="invoice-detail__value">{selectedQuote.phone}</p>
                )}
              </div>
              <div>
                <p className="invoice-detail__label">Quote Date</p>
                <p className="invoice-detail__value">
                  {format(new Date(selectedQuote.created_at), 'MMMM d, yyyy')}
                </p>
              </div>
            </div>

            <table className="table invoice-detail__table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Qty</th>
                  <th>Price</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {selectedQuote.items?.map((item, index) => (
                  <tr key={index}>
                    <td>{item.description}</td>
                    <td>{item.quantity}</td>
                    <td>${parseFloat(item.unit_price).toFixed(2)}</td>
                    <td>${parseFloat(item.total).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="invoice-summary">
              <div className="invoice-summary__row">
                <span>Subtotal</span>
                <span>${parseFloat(selectedQuote.subtotal).toFixed(2)}</span>
              </div>
              {selectedQuote.tax_rate > 0 && (
                <div className="invoice-summary__row">
                  <span>Tax ({(selectedQuote.tax_rate * 100).toFixed(2)}%)</span>
                  <span>${parseFloat(selectedQuote.tax_amount).toFixed(2)}</span>
                </div>
              )}
              <div className="invoice-summary__row invoice-summary__row--total">
                <span>Estimated Total</span>
                <span>${parseFloat(selectedQuote.total).toFixed(2)}</span>
              </div>
            </div>

            {selectedQuote.notes && (
              <div className="invoice-detail__notes">
                <p className="invoice-detail__label">Notes</p>
                <p>{selectedQuote.notes}</p>
              </div>
            )}

            <div className="modal__footer">
              <button
                className="btn btn--danger"
                onClick={() => handleDeleteQuote(selectedQuote.id)}
              >
                Delete
              </button>
              <button
                className="btn btn--outline"
                onClick={() => handleEditQuote(selectedQuote)}
              >
                Edit Quote
              </button>
              <button
                className="btn btn--outline"
                onClick={() => handleCopyQuote(selectedQuote.id)}
              >
                {copyQuoteLabel}
              </button>
              <button
                className="btn btn--primary"
                disabled={converting}
                onClick={() => handleConvertToInvoice(selectedQuote.id)}
              >
                {converting ? 'Converting...' : 'Convert to Invoice'}
              </button>
              <button
                className="btn btn--secondary"
                onClick={() => setShowDetailModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Quotes;
